import {
  CountryUtils,
  LabelUtils,
  NumberUtils,
  StringUtils,
} from '@spotify/confidence-core-react';

import { Criterion, CriterionInfo, Expression, TargetingValue } from './types';

export const hasChanged = (a: unknown, b: unknown): boolean => {
  if (!a && !b) return false;
  return JSON.stringify(a) !== JSON.stringify(b);
};

export const isEmptyExpression = (expr: Expression | undefined): boolean => {
  if (!expr) return true;
  if (expr.ref) return false;
  if (expr.not) return isEmptyExpression(expr.not);
  const operands = expr.and?.operands ?? expr.or?.operands ?? [];
  return operands.length === 0 || operands.every(o => isEmptyExpression(o));
};

export const formatAllocation = (
  value: number | string | undefined | null,
): string =>
  NumberUtils.formatPercentage(
    value !== undefined && value !== null ? Number(value) : value,
    'Not set',
  );

const formatTimestamp = (ts: string): string => {
  const date = new Date(ts);
  if (isNaN(date.getTime())) return ts;
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

const isCountryAttribute = (
  attributeName: string | null | undefined,
): boolean =>
  attributeName === 'registration_country' || attributeName === 'country';

const formatValue = (
  value: TargetingValue | null | undefined,
  isCountry?: boolean,
): string => {
  if (!value) return '';
  if (value.stringValue !== undefined && value.stringValue !== null) {
    return isCountry
      ? CountryUtils.getCountryLabel(value.stringValue)
      : value.stringValue;
  }
  if (value.numberValue !== undefined && value.numberValue !== null)
    return String(value.numberValue);
  if (value.boolValue !== undefined && value.boolValue !== null)
    return String(value.boolValue);
  if (value.timestampValue !== undefined && value.timestampValue !== null)
    return formatTimestamp(value.timestampValue);
  if (
    value.versionValue?.version !== undefined &&
    value.versionValue?.version !== null
  )
    return value.versionValue.version;
  return '';
};

const formatRuleValue = (attr: NonNullable<Criterion['attribute']>): string => {
  const country = isCountryAttribute(attr.attributeName);

  if (attr.eqRule?.value) {
    const v = formatValue(attr.eqRule.value, country);
    return v ? `is ${v}` : '';
  }

  if (attr.setRule?.values) {
    const values = attr.setRule.values
      .map(v => formatValue(v, country))
      .filter(Boolean);
    return values.length > 0 ? `in [${values.join(', ')}]` : '';
  }

  if (attr.rangeRule) {
    const parts: string[] = [];
    const r = attr.rangeRule;
    if (r.startInclusive) parts.push(`>= ${formatValue(r.startInclusive)}`);
    if (r.startExclusive) parts.push(`> ${formatValue(r.startExclusive)}`);
    if (r.endInclusive) parts.push(`<= ${formatValue(r.endInclusive)}`);
    if (r.endExclusive) parts.push(`< ${formatValue(r.endExclusive)}`);
    return parts.join(' and ');
  }

  if (attr.containsRule?.value) {
    return `contains ${attr.containsRule.value}`;
  }

  if (attr.startsWithRule?.value) {
    return `starts with ${attr.startsWithRule.value}`;
  }

  if (attr.endsWithRule?.value) {
    return `ends with ${attr.endsWithRule.value}`;
  }

  return '';
};

export const isHoldback = (criterion: Criterion): boolean =>
  LabelUtils.getLabelValue(criterion.segment?.labels ?? [], 'type') ===
  'holdback';

export const describeCriterion = (criterion: Criterion): CriterionInfo => {
  if (criterion.attribute) {
    const name =
      criterion.attribute.displayName ??
      (criterion.attribute.attributeName
        ? StringUtils.humanize(criterion.attribute.attributeName)
        : 'unknown');
    const rule = formatRuleValue(criterion.attribute);
    return { tagLabel: name, value: rule };
  }

  if (criterion.segment) {
    const name =
      criterion.segment.displayName ??
      criterion.segment.segment?.split('/').pop() ??
      'unknown';
    const label = isHoldback(criterion) ? 'Holdback' : 'Segment';
    return { tagLabel: label, value: name };
  }

  if (criterion.materializedSegment) {
    const name =
      criterion.materializedSegment.displayName ??
      criterion.materializedSegment.materializedSegment?.split('/').pop() ??
      'unknown';
    return { tagLabel: 'Materialized segment', value: name };
  }

  return { tagLabel: 'Unknown', value: '' };
};

const describeExpression = (
  expression: Expression,
  criteria: Record<string, Criterion>,
  nested: boolean,
): string => {
  if (expression.ref) {
    const criterion = criteria[expression.ref];
    if (!criterion) {
      return `Missing criterion: ${StringUtils.humanize(expression.ref)}`;
    }
    const { tagLabel, value } = describeCriterion(criterion);
    return [tagLabel, value].filter(Boolean).join(' ');
  }

  if (expression.not) {
    const description = describeExpression(expression.not, criteria, false);
    return description ? `not (${description})` : '';
  }

  const operator = expression.and ? 'and' : 'or';
  const operands = expression.and?.operands ?? expression.or?.operands ?? [];
  const descriptions = operands
    .map(operand => describeExpression(operand, criteria, true))
    .filter(Boolean);
  const description = descriptions.join(` ${operator} `);

  return nested && descriptions.length > 1 ? `(${description})` : description;
};

export const describeTargetingExpression = (
  expression: Expression | undefined,
  criteria: Record<string, Criterion>,
): string => {
  if (!expression || isEmptyExpression(expression)) return 'All users';
  return describeExpression(expression, criteria, false) || 'All users';
};
